import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import FancySelect from '@/common/components/inputs/FancySelect/FancySelect';
import validateEmail from '@/security/helpers/validateEmail';
import { EmailOnlyContact } from '@/security/types/EmailOnlyContact';
import { defaultIsEmailAllowed, isEmailInOptions, toAssigneeSelectOption } from './AssigneeSelect.utils';
import AssigneeSelectOption from './AssigneeSelectOption/AssigneeSelectOption';
import MultiValueAssigneeOption from './MultiValueAssigneeOption/MultiValueAssigneeOption';
import { useDebouncedLoadOptions } from './useDebouncedLoadOptions';
function AssigneeSelect(props) {
    const { value, onChange, disabled, isMulti = false, isEmailAllowed = defaultIsEmailAllowed, defaultOptions: defaultAssignees, loadAssignees, formatCreateLabel, placeholder = '', } = props;
    const defaultOptions = useMemo(() => defaultAssignees.map(toAssigneeSelectOption), [defaultAssignees]);
    const selectedOptions = useMemo(() => value.map(toAssigneeSelectOption), [value]);
    const loadOptions = useDebouncedLoadOptions(loadAssignees);
    const [inputValue, setInputValue] = useState('');
    const canAddEmail = useCallback((email) => {
        const trimmed = email.trim().toLowerCase();
        return validateEmail(email) && isEmailAllowed(trimmed) && !isEmailInOptions(trimmed, value);
    }, [isEmailAllowed, value]);
    const onCreateOption = useCallback((inputValue) => {
        const trimmed = inputValue.trim().toLowerCase();
        if (!canAddEmail(trimmed)) {
            return;
        }
        const newAssignee = new EmailOnlyContact(trimmed);
        onChange([...(value || []), newAssignee]);
    }, [value, onChange, canAddEmail]);
    const onChangeHandler = useCallback((options) => {
        if (!options) {
            onChange(null);
            return;
        }
        const assignees = options.map((option) => option.data);
        onChange(assignees);
    }, [onChange]);
    function onInputChangeHandler(value, { action }) {
        // prevent input value set to "" on close, so onBlueHandler can use last value
        if (action === 'menu-close' || action === 'input-blur') {
            return;
        }
        setInputValue(value.trim());
    }
    function onBlur() {
        onCreateOption(inputValue);
        setInputValue('');
    }
    const getNoOptionsMessage = useCallback(({ inputValue }) => {
        const trimmed = inputValue.trim().toLowerCase();
        if (!trimmed || !validateEmail(trimmed)) {
            return 'Enter email';
        }
        if (isEmailInOptions(trimmed, value) || !isEmailAllowed(trimmed)) {
            return 'Email is already added';
        }
        return '';
    }, [value, isEmailAllowed]);
    return (_jsx(FancySelect, { defaultOptions: defaultOptions, formatCreateLabel: formatCreateLabel, hideArrow: true, inputValue: inputValue, disabled: disabled, placeholder: placeholder, isMulti: isMulti, isCreatable: true, isValidNewOption: canAddEmail, loadOptions: loadOptions, noOptionsMessage: getNoOptionsMessage, onChange: onChangeHandler, onCreateOption: onCreateOption, onBlur: onBlur, onInputChange: onInputChangeHandler, renderMultiValueOption: (option) => _jsx(MultiValueAssigneeOption, { option: option }), renderOption: (option) => _jsx(AssigneeSelectOption, { option: option }), value: selectedOptions }));
}
export default observer(AssigneeSelect);
